import React, { useEffect, useMemo, useState } from "react";
import { GistType } from "../../services/types";
import DiscussionMapTree from "./DiscussionMapTree";
import DiscussionMapPreview from "./DiscussionMapPreview";
import DiscussionMapControlPanel from "./DiscussionMapControlPanel";

export type TreeDepthOption = "1" | "2" | "3" | "all";

export interface ActivityMeta {
  lastActivityAt: number | null;
  isRecentSelfOrSubtree: boolean;
  latestReason?: string;
}

const RECENT_THRESHOLD = 7 * 24 * 60 * 60 * 1000;

export const buildActivityMap = (gists: GistType[]): Map<number, ActivityMeta> => {
  const map = new Map<number, ActivityMeta>();
  const now = Date.now();

  const parseDate = (d: string | undefined | null) => d ? Date.parse(d) : null;

  const compute = (node: GistType): ActivityMeta => {
    let latest = parseDate(node.createdAt) || 0;
    let reason = "GIST created";

    if (node.versions) {
      for (const v of node.versions) {
        const vDate = parseDate(v.createdAt);
        if (vDate && vDate > latest) {
          latest = vDate;
          reason = "new version";
        }
        if (v.edits) {
          for (const e of v.edits) {
            const eDate = parseDate(e.createdAt);
            if (eDate && eDate > latest) {
              latest = eDate;
              reason = "new edit";
            }
            if (e.comments) {
              for (const c of e.comments) {
                const cDate = parseDate(c.createdAt);
                if (cDate && cDate > latest) {
                  latest = cDate;
                  reason = "new comment";
                }
              }
            }
          }
        }
      }
    }

    let isRecent = latest > 0 && (now - latest < RECENT_THRESHOLD);

    if (Array.isArray(node.gists)) {
      for (const child of node.gists) {
        const childMeta = compute(child);
        if (childMeta.isRecentSelfOrSubtree) {
          isRecent = true;
        }
        if (childMeta.lastActivityAt && childMeta.lastActivityAt > latest) {
          latest = childMeta.lastActivityAt;
          if (childMeta.latestReason === "GIST created") {
            reason = "new reply";
          } else {
            reason = "recent activity in branch";
          }
        }
      }
    }

    const meta = {
      lastActivityAt: latest > 0 ? latest : null,
      isRecentSelfOrSubtree: isRecent,
      latestReason: latest > 0 ? reason : undefined
    };
    map.set(node.id, meta);
    return meta;
  };

  for (const gist of gists) {
    compute(gist);
  }

  return map;
};

interface DiscussionMapProps {
  gists: GistType[];
  onOpenFullGist?: (path: number[]) => void;
}

const findPathToGist = (nodes: GistType[], targetId: number | null): number[] | null => {
  if (!targetId) return null;
  
  const dfs = (currentList: GistType[], currentPath: number[]): number[] | null => {
    for (const node of currentList) {
      currentPath.push(node.id);
      if (node.id === targetId) {
        return [...currentPath];
      }
      if (Array.isArray(node.gists) && node.gists.length > 0) {
        const found = dfs(node.gists, currentPath);
        if (found) return found;
      }
      currentPath.pop();
    }
    return null;
  };
  
  return dfs(nodes, []);
};

const DiscussionMap: React.FC<DiscussionMapProps> = ({ gists, onOpenFullGist }) => {
  const [activeGist, setActiveGist] = useState<GistType | null>(() => gists?.[0] ?? null);
  const [treeDepth, setTreeDepth] = useState<TreeDepthOption>("3");
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [focusSelectedBranch, setFocusSelectedBranch] = useState(false);
  const [collapseSiblings, setCollapseSiblings] = useState(false);
  const [showRecentOnly, setShowRecentOnly] = useState(false);

  const activityMap = useMemo(() => buildActivityMap(gists), [gists]);

  useEffect(() => {
    setActiveGist((current) => {
      if (current && findPathToGist(gists, current.id)) {
        return current;
      }

      return gists?.[0] ?? null;
    });
    setPreviewCollapsed(false);
  }, [gists]);

  const handleSelect = (gist: GistType) => {
    setActiveGist((prev) => (prev?.id === gist.id ? null : gist));
  };

  const handleOpenFullGist = (gist: GistType) => {
    if (!onOpenFullGist) return;

    const path = findPathToGist(gists, gist.id);
    if (path) {
      onOpenFullGist(path);
    }
  };

  return (
    <div className="discussion-map-shell">
      {/* Left column: Navigation Tree */}
      <aside className={`discussion-map-tree-panel ${previewCollapsed ? "is-expanded" : ""}`}>
        <div className="discussion-map-tree-panel-header">
          <div className="discussion-map-tree-panel-kicker">Discussion Map</div>
          <p className="discussion-map-tree-panel-hint">
            Click a GIST to preview it. Double-click to open it in Reader View →
          </p>
        </div>
        <div className="discussion-map-tree-panel-scroll">
          <DiscussionMapTree
            gists={gists}
            activeId={activeGist?.id ?? null}
            onSelect={handleSelect}
            onOpenFullGist={handleOpenFullGist}
            maxDepth={treeDepth}
            focusSelectedBranch={focusSelectedBranch}
            collapseSiblings={collapseSiblings}
            showRecentOnly={showRecentOnly}
            activityMap={activityMap}
          />
        </div>
      </aside>

      {/* Right column: GIST Preview */}
      <main className={`discussion-map-preview-panel${previewCollapsed ? " is-collapsed" : ""}`}>
        <DiscussionMapPreview 
          gist={activeGist} 
          collapsed={previewCollapsed}
          onCollapse={() => setPreviewCollapsed(true)}
          onExpand={() => setPreviewCollapsed(false)}
          onOpenFullGist={() => {
            if (activeGist) {
              handleOpenFullGist(activeGist);
            }
          }}
          activityMeta={activeGist ? activityMap.get(activeGist.id) : undefined}
        />
      </main>

      {/* Control panel — collapsed by default */}
      <DiscussionMapControlPanel 
        treeDepth={treeDepth}
        setTreeDepth={setTreeDepth}
        isOpen={controlsOpen}
        setIsOpen={setControlsOpen}
        focusSelectedBranch={focusSelectedBranch}
        setFocusSelectedBranch={setFocusSelectedBranch}
        collapseSiblings={collapseSiblings}
        setCollapseSiblings={setCollapseSiblings}
        showRecentOnly={showRecentOnly}
        setShowRecentOnly={setShowRecentOnly}
      />
    </div>
  );
};

export default DiscussionMap;
